<template>
    <div class="map">
        <div ref="map" v-html="svgMap" />
        <div class="map__legend" v-if="legend" aria-hidden="true">
            <div class="map__legend-row" v-for="(row, index) in legend" :key="index" >
                <div class="map__legend-color" :style="`background-color: ${row.color};`"></div>
                {{ row.name }}
            </div>
        </div>
    </div>
</template>

<script setup>
    import { onMounted, ref, watch } from 'vue';
    import svgMap from '../assets/map_statnett.svg';
    import tippy, { followCursor }  from 'tippy.js';
    import 'tippy.js/dist/tippy.css';

    const AREA_SELECTED_CSS_CLASS = '__selected';
    const ARROW_VISIBLE_CSS_CLASS = '__visible';
    const FRAME_SELECTED_CSS_CLASS = '__selected';

    const TIPPY_CONFIG = {
        arrow: true,
        followCursor: true,
        plugins: [followCursor],
        placement: 'bottom-start',
        offset: [0, 25],
        theme: 'statnett',
        aria: null,
    };

    const props = defineProps({
        areas: {
            type: Array,
            default: () => [],
        },
        selectedAreaId: {
            type: [String, Number],
            default: null,
        },
        arrows: {
            type: Array,
            default: () => [],
        },
        legend: {
            type: Array,
            default: null,
        },
        clickable: {
            type: Boolean,
            default: false,
        },
    });
    const emit = defineEmits(['click', 'area-click']);
    const map = ref(null);
    const tippys = {};

    watch(() => props.areas, (areas) => {
        (areas || []).forEach(area => {
            initArea(area.id);
            fill(area);
            tooltip(area);
        });
    }, {
        immediate: true,
    });

    watch(() => props.selectedAreaId, (newVal, oldVal) => {
        deselect(oldVal);
        select(newVal);
    });

    watch(() => props.arrows, () => {
        updateArrows();
    }, {
        immediate: true,
    });

    onMounted(() => {
        initAreas();

        select(props.selectedAreaId); // First selection

        updateArrows();
        updateTexts();
    });

    /**
     * Get area el
     */
    function getAreaEl(areaId) {
        if(!map.value) {
            return;
        }

        return map.value.querySelector(`g[data-name="Map"] g[data-name="${areaId}"]`);
    }

    /**
     * Get arrow el
     */
    function getArrowEl(arrowName) {
        if(!map.value) {
            return;
        }

        return map.value.querySelector(`g[data-name="Arrows"] g[data-name="${arrowName}"]`);
    }

    /**
     * Get all arrow els
     */
    function getAllArrowEls() {
        if(!map.value) {
            return;
        }

        return [].map.call(
            map.value.querySelectorAll(`g[data-name="Arrows"] g[data-name]`),
            (el) => el
        );
    }

    /**
     * Get frame el
     */
    function getFrameEl(areaId) {
        if(!map.value) {
            return;
        }

        return map.value.querySelector(`g[data-name="Frame text"] [data-name="Frame ${areaId}"]`);
    }

    /**
     * Init areas
     */
    function initAreas() {
        (props.areas || []).forEach(area => {
            initArea(area.id);
            fill(area);
            tooltip(area);
        });
    }

    /**
     * Init area
     */
    function initArea(areaId) {
        const el = getAreaEl(areaId);

        if(!el) {
            return;
        }

        const area = props.areas.find(currentArea => currentArea.id === areaId);

        raise(el);

        if(props.clickable) {
            el.classList.add('__clickable');
            el.setAttribute('role', 'button');
            el.setAttribute('tabindex', '0');
            el.setAttribute('focusable', 'true');

            if(!el.hasAttribute('aria-label')) {
                el.setAttribute('aria-label', area && area.name ? `${area.name} (${area.id})` : areaId);
            }

            if(el.__statnettClickBound) {
                return;
            }

            const activateArea = () => {
                emit('click', areaId);
                emit('area-click', areaId);
            };

            el.addEventListener('click', activateArea);
            el.addEventListener('keydown', (event) => {
                if(event.key !== 'Enter' && event.key !== ' ') {
                    return;
                }

                event.preventDefault();
                activateArea();
            });

            el.__statnettClickBound = true;
        }
    }

    /**
     * Select (area)
     */
    function select(areaId) {
        const areaEl = getAreaEl(areaId);
        const frameAreaEl = getFrameEl(areaId);

        areaEl && areaEl.classList.add(AREA_SELECTED_CSS_CLASS);
        areaEl && raise(areaEl);

        frameAreaEl && frameAreaEl.classList.add(FRAME_SELECTED_CSS_CLASS);
    }

    /**
     * Deselect (area)
     */
    function deselect(areaId) {
        const areaEl = getAreaEl(areaId);
        const frameAreaEl = getFrameEl(areaId);

        areaEl && areaEl.classList.remove(AREA_SELECTED_CSS_CLASS);
        frameAreaEl && frameAreaEl.classList.remove(FRAME_SELECTED_CSS_CLASS);
    }

    /**
     * Fill (area)
     */
    function fill(area) {
        const el = getAreaEl(area.id);

        if(!el) {
            return;
        }

        const shapeEls = el.querySelectorAll('path, polygon, polyline');

        [].forEach.call(shapeEls, (pathEl) => {
            if(area.color) {
                pathEl.style.fill = area.color;
            } else {
                pathEl.style.removeProperty('fill');
            }
        });
    }

    /**
     * Tooltip
     */
    function tooltip(area) {
        if(!area.tooltip) {
            return;
        }

        const el = getAreaEl(area.id);

        if(!el) {
            return;
        }

        el.ariaLabel = `${area.name}: ${area.tooltip}`;

        if(tippys[area.id]) {
            tippys[area.id].setContent(area.tooltip);
        } else {
            tippys[area.id] = tippy(el, {
                content: area.tooltip,
                ...TIPPY_CONFIG,
            });
        }
    }

    /**
     * Update arrows
     */
    function updateArrows() {
        const arrowEls = getAllArrowEls();

        [].forEach.call(arrowEls || [], (el) => {
            el.classList.remove(ARROW_VISIBLE_CSS_CLASS);
        });

        (props.arrows || []).forEach(arrow => {
            const el = getArrowEl(arrow);

            if(!el) {
                return;
            }

            el.classList.add(ARROW_VISIBLE_CSS_CLASS);
        });
    }

    /**
     * Update texts
     */
    function updateTexts() {
        if(!map.value) {
            return;
        }

        const frameEls = map.value.querySelectorAll('g[data-name="Frame text"] [data-name]');

        [].forEach.call(frameEls, (frameEl) => {
            const areaId = frameEl.dataset.name.split(' ')[1]; // ['Frame', 'NO1'][1]

            addText(frameEl, areaId);
        });
    }

    function raise(el) {
        el.parentNode.appendChild(el);
    }

    function addText(path, text) {
        var t = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        var b = path.getBBox();

        t.setAttribute('transform', `translate(${b.x + 5}, ${b.y + b.height / 2 + 6})`);

        t.textContent = text;

        t.setAttribute('font-size', '16');
        t.setAttribute('aria-hidden', 'true');
        t.classList.add('__text');

        path.parentNode.insertBefore(t, path.nextSibling);
    }
</script>
